<template>
  <br />
  <div class="col-sm-12">
    <!-- Tabla de presentación -->  
    <div class="card">

      <div class="card-header">
        <div class="d-flex align-items-center gap-3">
          <img
            v-if="aseguradoraLogo"
            :src="aseguradoraLogo"
            :alt="producto.aseguradoraNombre"
            class="aseguradora-icon"
          />
          <div>
            <h3 class="mb-2">{{ producto ? producto.name : 'Producto #' + productId }}</h3>
            <h5 v-if="producto" class="text-muted mb-2">
              {{ producto.description }}
            </h5>
            <p v-if="producto" class="text-muted mb-0">              
              {{ producto.ramoNombre }} | {{ producto.tipoNombre }} | {{ producto.divisaSimbolo }} |
              Plazo {{ producto.plazo }} año | Plan {{ producto.plan }}
            </p>
          </div>          
        </div>                
      </div>

      <div class="card-body">
        <!-- Selector de periodo (izquierda) e iconos para descargar la tabla en PDF y XLS (derecha) -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div v-if="periodos.length > 0" class="d-flex align-items-center gap-2">
            <label for="periodoTarifas" class="form-label fw-bold mb-0">Periodo</label>
            <select
              id="periodoTarifas"
              class="form-select form-select-sm w-auto"
              :value="periodoNombre"
              @change="elegirPeriodo($event.target.value)"
            >
              <option
                v-for="o in opcionesPeriodo"
                :key="'periodo-' + o.nombre"
                :value="o.nombre"
                :selected="o.nombre === periodoNombre"
              >{{ o.texto }}</option>
            </select>
            <small v-if="cargandoPeriodo" class="text-muted">Cargando tarifas...</small>
          </div>
          <div v-else></div>
          <div>
            <button class="btn btn-sm btn-danger me-2" @click="exportToPDF">
              <vue-feather type="file-text" size="16"></vue-feather> PDF
            </button>
            <button class="btn btn-sm btn-success" @click="exportToExcel">
              <vue-feather type="file" size="16"></vue-feather> XLS
            </button>
          </div>
        </div>
        <div v-if="periodoNoVigente" class="alert alert-warning py-2 mb-3">Periodo no vigente hoy</div>
        <div class="table-responsive" :class="{ 'opacity-50': cargandoPeriodo }" v-if="!loading && mostrarMatriz">
          <!-- Tabla normal: Rango en filas, Deducibles en columnas -->
          <table v-if="!isPivoted" class="table table-bordered table-hover tarifa-matrix">
            <thead>
              <!-- Fila 1: Header Deducibles combinado -->
              <tr class="border-bottom-primary">
                <th rowspan="3" class="align-middle text-center">Edad</th>                
                <th :colspan="deducibles.length" class="text-center">Deducibles</th>
              </tr>
              <!-- Fila 2: Valores de deducible -->
              <tr>
                <th v-for="ded in deducibles" :key="'ded-'+ded" class="text-center">
                  {{ formatCurrency(ded) }}
                </th>
              </tr>
              <!-- Fila 3: Factores -->
              <tr>
                <th v-for="ded in deducibles" :key="'factor-'+ded" class="text-end text-muted">                  
                  Prima neta
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rango in rangos" :key="rango">
                <td class="fw-bold text-center">{{ rango }}</td>
                <td v-for="ded in deducibles" :key="rango+'-'+ded" class="text-end">
                  {{ getTarifa(rango, ded) }}
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Tabla pivoteada (producto 3): Suma asegurada en filas, Rangos en columnas -->
          <table v-else class="table table-bordered table-hover tarifa-matrix">
            <thead>
              <tr class="border-bottom-primary">
                <th class="align-middle text-center">Suma asegurada</th>
                <th v-for="rango in rangos" :key="'rango-'+rango" class="text-center">{{ rango }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ded in deducibles" :key="ded">
                <td class="fw-bold text-center">{{ formatCurrency(ded) }}</td>
                <td v-for="rango in rangos" :key="rango+'-'+ded" class="text-end">
                  {{ getTarifa(rango, ded) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="loading" class="text-center py-4">
          <span>Cargando datos...</span>
        </div>
        <div v-if="!loading && !error && !mostrarMatriz" class="alert alert-info">
          Este producto no tiene tarifas cargadas.
        </div>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
      </div>
      
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <div>
            <small>Total de rangos: {{ rangos.length }}</small>
            <small> | {{ isPivoted ? 'Sumas aseguradas' : 'Deducibles' }}: {{ deducibles.length }}</small>
          </div>
        </div>
      </div>

    </div>
    
  </div>

</template>

<script>
import api from '@/axios';
import { exportMatrixToExcel, exportMatrixToPDF } from '@/utils/exportUtils';
import { getAseguradoraLogo } from '@/utils/aseguradoraLogo';
import {
  normalizarPeriodos,
  periodoPorDefecto,
  estaVigente,
  fechaConsulta,
  filtrarPorPeriodo,
} from '@/utils/periodosTarifas';

export default {
  name: 'productosDetalle',
  data() {
    return {
      productId: null,
      producto: null,
      tarifas: [],
      // Periodos del producto y el que muestra la matriz (nombre; '' = "Sin periodo").
      periodos: [],
      periodoNombre: null,
      cargandoPeriodo: false,
      // Tarifas ya consultadas por periodo y contador para descartar respuestas tardías.
      tarifasPorPeriodo: {},
      seleccionId: 0,
      loading: false,
      error: null,
    };
  },

  computed: {
    // Periodo que muestra la matriz (objeto de `periodos`) y sus tarifas.
    periodoActual() {
      return this.periodos.find(p => p.nombre === this.periodoNombre) || null;
    },
    tarifasDelPeriodo() {
      return filtrarPorPeriodo(this.tarifas, this.periodoNombre);
    },
    // Opciones del combo; el periodo vigente hoy se marca.
    opcionesPeriodo() {
      const hoy = new Date();
      return this.periodos.map(p => ({
        nombre: p.nombre,
        texto: estaVigente(p, hoy) ? `${p.etiqueta} (vigente)` : p.etiqueta,
      }));
    },
    // Aviso solo cuando el periodo mostrado tiene fechas y hoy queda fuera de ellas.
    periodoNoVigente() {
      const p = this.periodoActual;
      return !!(p && p.inicio && p.fin && !estaVigente(p, new Date()));
    },
    // Nombre del periodo mostrado para los exportes; "Sin periodo" y sin selección no agregan sufijo.
    periodoExportable() {
      return this.periodoActual && this.periodoActual.nombre ? this.periodoActual.nombre : '';
    },
    mostrarMatriz() {
      return this.periodos.length > 0 || this.tarifas.length > 0;
    },

    // Obtener deducibles únicos ordenados
    deducibles() {
      const uniqueDeducibles = [...new Set(this.tarifasDelPeriodo.map(t => t.deducible))];
      return uniqueDeducibles.sort((a, b) => a - b);
    },

    // Obtener rangos de edad únicos ordenados
    rangos() {
      const uniqueRangos = [...new Set(this.tarifasDelPeriodo.map(t => t.edadRango))];
      // Ordenar rangos por el primer número
      return uniqueRangos.sort((a, b) => {
        const numA = parseInt(a.split('-')[0]);
        const numB = parseInt(b.split('-')[0]);
        return numA - numB;
      });
    },

    // Mapa de deducible -> factor
    factoresByDeducible() {
      const map = {};
      this.tarifasDelPeriodo.forEach(t => {
        if (!map[t.deducible]) {
          map[t.deducible] = t.factor;
        }
      });
      return map;
    },

    // Mapa para acceso rápido a tarifas
    tarifaMap() {
      const map = {};
      this.tarifasDelPeriodo.forEach(t => {
        const key = `${t.edadRango}-${t.deducible}`;
        map[key] = t.tarifa;
      });
      return map;
    },

    isPivoted() {
      return String(this.productId) === '3';
    },

    aseguradoraLogo() {
      return this.producto ? getAseguradoraLogo(this.producto.aseguradoraNombre) : null;
    },
  },

  created() {
    // Obtener el ID del producto de la URL
    this.productId = this.$route.params.id;
    this.loadData();
  },

  methods: {
    async fetchData() {
      try {
        this.error = null;
        const response = await api.get(`productos/${this.productId}/detalles`);
        return response.data || [];
      } catch (err) {
        console.error('Error fetching data:', err);
        this.error = 'No se pudieron cargar los datos.';
        return [];
      }
    },

    // Tarifas vigentes a una fecha (yyyy-MM-dd) del periodo elegido; sin fecha, las vigentes hoy.
    async pedirTarifas(fecha) {
      const response = await api.get(`productos/${this.productId}/detalles`, { params: { fecha } });
      return (response.data && response.data.tarifas) || [];
    },

    formatCurrency(value) {
      if (value == null) return '';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(value);
    },

    async loadData() {
      this.loading = true;
      try {
        await this.cargarDatos();
      } finally {
        this.loading = false;
      }
    },

    async cargarDatos() {
      const data = await this.fetchData();
      // Guardar los datos del producto
      this.producto = data;
      // Tarifas vigentes hoy (el backend filtra por vigencia) y todos los periodos del producto
      this.tarifas = data.tarifas || [];
      this.periodos = normalizarPeriodos(data.periodos, this.tarifas);

      const porDefecto = periodoPorDefecto(this.periodos, new Date());
      if (!porDefecto) return;
      this.periodoNombre = porDefecto.nombre;
      if (estaVigente(porDefecto, new Date())) {
        // Ya se tienen sus tarifas: no hace falta otra llamada.
        this.tarifasPorPeriodo[porDefecto.nombre] = filtrarPorPeriodo(this.tarifas, porDefecto.nombre);
      } else {
        // Ninguno vigente hoy: se pide el más reciente para no mostrar la matriz vacía.
        await this.cargarTarifasDelPeriodo(porDefecto);
      }
    },

    elegirPeriodo(nombre) {
      const periodo = this.periodos.find(p => p.nombre === nombre);
      if (!periodo) return;
      this.cargarTarifasDelPeriodo(periodo);
    },

    // Muestra las tarifas del periodo; la última selección manda aunque lleguen respuestas tardías.
    async cargarTarifasDelPeriodo(periodo) {
      const seleccion = ++this.seleccionId;
      const anterior = this.periodoNombre;
      this.periodoNombre = periodo.nombre;
      this.error = null;

      const cache = this.tarifasPorPeriodo[periodo.nombre];
      if (cache) {
        this.tarifas = cache;
        this.cargandoPeriodo = false;
        return;
      }

      this.cargandoPeriodo = true;
      try {
        const tarifas = await this.pedirTarifas(fechaConsulta(periodo));
        if (seleccion !== this.seleccionId) return;
        this.tarifasPorPeriodo[periodo.nombre] = filtrarPorPeriodo(tarifas, periodo.nombre);
        this.tarifas = tarifas;
      } catch (err) {
        if (seleccion !== this.seleccionId) return;
        console.error('Error fetching data:', err);
        this.error = 'No se pudieron cargar los datos.';
        this.periodoNombre = anterior;
      } finally {
        if (seleccion === this.seleccionId) this.cargandoPeriodo = false;
      }
    },

    // Obtener tarifa formateada para un rango y deducible específico
    getTarifa(rango, deducible) {
      const key = `${rango}-${deducible}`;
      const tarifa = this.tarifaMap[key];
      return tarifa != null ? this.formatCurrency(tarifa) : '-';
    },

    exportToExcel() {
      let headers, subHeaders, rows;
      if (this.isPivoted) {
        headers = ['Suma asegurada', ...this.rangos];
        subHeaders = [];
        rows = this.deducibles.map(ded => [
          this.formatCurrency(ded),
          ...this.rangos.map(rango => this.getTarifa(rango, ded))
        ]);
      } else {
        headers = ['Rango', ...this.deducibles.map(d => this.formatCurrency(d))];
        subHeaders = ['Factor', ...this.deducibles.map(d => this.factoresByDeducible[d])];
        rows = this.rangos.map(rango => [
          rango,
          ...this.deducibles.map(ded => this.getTarifa(rango, ded))
        ]);
      }

      // Nombre del archivo (con el periodo mostrado)
      const base = this.producto ? `Tarifas_${this.producto.name}` : `Tarifas_Producto_${this.productId}`;
      const title = this.periodoExportable ? `${base}_${this.periodoExportable}` : base;

      exportMatrixToExcel({ headers, subHeaders, rows, title });
    },

    exportToPDF() {
      let headers, subHeaders, rows;
      if (this.isPivoted) {
        headers = ['Suma asegurada', ...this.rangos];
        subHeaders = [];
        rows = this.deducibles.map(ded => [
          this.formatCurrency(ded),
          ...this.rangos.map(rango => this.getTarifa(rango, ded))
        ]);
      } else {
        headers = ['Rango', ...this.deducibles.map(d => this.formatCurrency(d))];
        subHeaders = ['Factor', ...this.deducibles.map(d => this.factoresByDeducible[d])];
        rows = this.rangos.map(rango => [
          rango,
          ...this.deducibles.map(ded => this.getTarifa(rango, ded))
        ]);
      }

      // Título y subtítulo (con el periodo mostrado)
      const base = this.producto ? `Tarifas - ${this.producto.name}` : `Tarifas - Producto ${this.productId}`;
      const title = this.periodoExportable ? `${base} (${this.periodoExportable})` : base;
      const detalle = this.producto
        ? `${this.producto.aseguradoraNombre} | ${this.producto.ramoNombre} | ${this.producto.tipoNombre} | ${this.producto.divisaSimbolo}`
        : '';
      const subtitle = this.periodoExportable ? [detalle, `Periodo ${this.periodoExportable}`].filter(Boolean).join(' | ') : detalle;

      // Logo de la aseguradora (si existe)
      const logoSrc = this.aseguradoraLogo || '';

      // Logo de la aplicación
      let appLogoSrc = '';
      try {
        appLogoSrc = require('@/assets/images/logo/logo-login.png');
      } catch (e) {
        console.warn('Logo de aplicación no encontrado');
      }

      exportMatrixToPDF({ title, subtitle, headers, subHeaders, rows, logoSrc, appLogoSrc });
    },
  },
};

</script>

<style scoped>
.aseguradora-icon {
  width: 60px;
  height: 60px;
  object-fit: contain;
}
.tarifa-matrix th,
.tarifa-matrix td {
  white-space: nowrap;
  vertical-align: middle;
}
.tarifa-matrix thead th {
  background-color: #f8f9fa;
}
</style>
